import React, { useState } from "react";
import { Course, Player, ScoreMap, Hole } from "./types";
import ScoreScrollPicker from "./modals/ScoreScrollPicker";
import PlayersModal from "./modals/PlayersModal";
import TeesModal from "./modals/TeesModal";
import GamesModal from "./modals/GamesModal";
import LeaderboardModal from "./modals/LeaderboardModal";
import OoslerSelectionModal from "./modals/OoslerSelectionModal";
import WolfSelectionModal from "./modals/WolfSelectionModal";
import BingoSelectionModal from "./modals/BingoSelectionModal";
import TeeDot from "./TeeDot";
import {
  ActiveGame,
  BingoSelection,
  BingoSelections,
  OoslerSelections,
  WolfSelection,
  WolfSelections,
} from "./activeRound";
import { adjustHandicaps } from "./handicapAdjust";

const MAX_PLAYERS = 5;
const DEFAULT_VISIBLE_ROWS = 4;

interface Props {
  course: Course;
  // Round data is owned by the parent (CaddieGames) so it can persist
  // across navigation. Scorecard reads via props and writes via the
  // matching change callbacks — same shape as React's useState setters
  // so the existing functional-update code paths keep working unchanged.
  players: Player[];
  scores: ScoreMap;
  displayTeeId?: string;
  // Active games configured for this round. Forwarded to GamesModal
  // (toggle + options) and LeaderboardModal (per-game point columns).
  // Optional — readOnly history rounds may not carry games.
  games?: ActiveGame[];
  // Per-hole Oosler selections. Lives on the active round so it
  // persists across navigation / app restarts and rehydrates with the
  // rest of the round. Optional — only present when the Oosler game
  // has been enabled at least once.
  ooslerSelections?: OoslerSelections;
  // Per-hole Wolf decisions — same persistence story as ooslerSelections.
  wolfSelections?: WolfSelections;
  // Per-hole Bingo Bango Bongo winners — same persistence story.
  bingoSelections?: BingoSelections;
  onPlayersChange: React.Dispatch<React.SetStateAction<Player[]>>;
  onScoresChange: React.Dispatch<React.SetStateAction<ScoreMap>>;
  onDisplayTeeChange: (id: string | undefined) => void;
  onGamesChange?: (next: ActiveGame[]) => void;
  onOoslerSelectionsChange?: (next: OoslerSelections) => void;
  onWolfSelectionsChange?: (next: WolfSelections) => void;
  onBingoSelectionsChange?: (next: BingoSelections) => void;
  onChangeCourse: () => void;
  onEndRound: () => void;
  readOnly?: boolean;
  // Tournament rounds: the group is fixed by the admin, so players, handicaps
  // and tees can't be edited (scores + side games stay editable).
  lockRoster?: boolean;
  // The tournament's games. When present, a "Tournament" button shows beside
  // "Leaderboard" and opens a leaderboard scored on these games (independent
  // of the player's own side `games`).
  tournamentGames?: ActiveGame[];
  // Tournament + group ids — let the leaderboard submit this group's scores and
  // fetch every group's to build the cross-group tournament standings.
  tournamentId?: string;
  tournamentGroupId?: string;
  tournamentGroupName?: string;
  // Full-width header rows inserted before the player at each `startIndex`,
  // dividing a combined tournament card into its groups (e.g. "Group 1 · 2:00").
  groupBreaks?: { startIndex: number; label: string }[];
}

const newPlayerID = () => `p_${Math.random().toString(36).slice(2, 10)}`;

// Course handicap from handicap index. WHS formula:
//   CH = round( HI * (slope / 113) + (rating - par) )
// Falls back to round(HI) when the tee has no slope/rating, so courses
// without those numbers still distribute strokes against the HI directly.
function courseHandicap(
  handicapIndex: number | undefined,
  slope: number | undefined,
  rating: number | undefined,
  totalPar: number,
): number {
  if (handicapIndex == null) return 0;
  const slopeFactor = slope ? slope / 113 : 1;
  const ratingDiff = rating != null ? rating - totalPar : 0;
  return Math.round(handicapIndex * slopeFactor + ratingDiff);
}

// Number of shots a player receives on a given hole, given a course handicap.
//   CH 1..18  → 1 shot on holes where SI <= CH
//   CH 19..36 → 1 shot every hole + 2nd shot on holes where SI <= (CH - 18)
//   CH 37..54 → 2 shots every hole + 3rd shot on holes where SI <= (CH - 36)
//   CH 55+    → 3 shots every hole + 4th on the lowest holes; the indicator
//               caps at ★★★ but the underlying count is preserved.
function strokesFor(courseHcp: number, hole: Hole): number {
  if (courseHcp <= 0) return 0;
  const base = Math.floor(courseHcp / 18);
  const remainder = courseHcp % 18;
  return base + (hole.si <= remainder ? 1 : 0);
}

export default function Scorecard({
  course,
  players,
  scores,
  displayTeeId,
  games,
  ooslerSelections,
  wolfSelections,
  bingoSelections,
  onPlayersChange: setPlayers,
  onScoresChange: setScores,
  onDisplayTeeChange: setDisplayTeeId,
  onGamesChange,
  onOoslerSelectionsChange,
  onWolfSelectionsChange,
  onBingoSelectionsChange,
  onChangeCourse,
  onEndRound,
  readOnly = false,
  lockRoster = false,
  tournamentGames,
  tournamentId,
  tournamentGroupId,
  tournamentGroupName,
  groupBreaks,
}: Props) {
  // Roster (players / handicaps / tees) is non-editable in read-only history
  // views AND in tournament rounds (admin owns the group). Scoring stays open
  // in tournament rounds — only readOnly disables score entry.
  const rosterLocked = readOnly || lockRoster;
  // Hole whose Oosler selection modal is currently open + the score
  // cell's rect captured at trigger time, so the picker can anchor over
  // the same spot the user just tapped (the scoreCell is cleared as
  // part of saveScore, so we snapshot the rect before that fires).
  const [ooslerPrompt, setOoslerPrompt] = useState<{
    hole: Hole;
    anchorRect: DOMRect;
  } | null>(null);

  const ooslerEnabled = !!games?.some(
    (g) => g.enabled && g.id === "ooslerWoosler",
  );

  // Wolf per-hole picker — same trigger story as Oosler, but fires on EVERY
  // hole (the Wolf rotates all 18). The Wolf for a hole is players[(hole−1) %
  // count] in seating order.
  const [wolfPrompt, setWolfPrompt] = useState<{
    hole: Hole;
    anchorRect: DOMRect;
  } | null>(null);

  const wolfGame = games?.find((g) => g.enabled && g.id === "wolf");
  const wolfEnabled = !!wolfGame;
  // Blind Wolf is offered in the picker unless the round turned it off.
  const wolfBlindEnabled = wolfGame?.options?.blindEnabled !== false;

  const wolfForHole = (holeNumber: number): Player | undefined =>
    players.length > 0
      ? players[(holeNumber - 1) % players.length]
      : undefined;

  // Bingo Bango Bongo — three live picks per hole, fired on the first score
  // (same as Oosler/Wolf). Every hole awards all three.
  const [bingoPrompt, setBingoPrompt] = useState<{
    hole: Hole;
    anchorRect: DOMRect;
  } | null>(null);

  const bbbEnabled = !!games?.some(
    (g) => g.enabled && g.id === "bingoBangoBongo",
  );
  const puttingEnabled = !!games?.some(
    (g) => g.enabled && g.id === "putting",
  );

  // Local UI state — these don't need to survive navigation, so they
  // stay inside the component (unlike round data which lives upstream).
  const [editingNameIdx, setEditingNameIdx] = useState<number | null>(null);
  const [nameDraft, setNameDraft] = useState("");

  const [scoreCell, setScoreCell] = useState<{
    player: Player;
    hole: Hole;
    rect: DOMRect;
  } | null>(null);
  const [openModal, setOpenModal] = useState<
    "players" | "tees" | "games" | "leaderboard" | null
  >(null);

  const totalPar = course.holes.reduce((s, h) => s + h.par, 0);

  // Default tee = middle of the course tee list, falling back to first.
  const tees = course.tees || [];
  const defaultTeeId =
    tees.length > 0
      ? tees[Math.floor(tees.length / 2)].id
      : undefined;

  // Yds row tee, in order of preference:
  //   1. user's manual override (clicked Yds → picked a tee)
  //   2. player 1's selected tee
  //   3. course default tee
  // Per-hole yardage falls back further to legacy Hole.yardage when
  // the chosen tee has no yardages array.
  const yardageTeeId = displayTeeId || players[0]?.tee || defaultTeeId;
  const yardageTee = tees.find((t) => t.id === yardageTeeId);
  const fallbackTee = defaultTeeId
    ? tees.find((t) => t.id === defaultTeeId)
    : undefined;
  const displayedYardages: (number | undefined)[] = course.holes.map(
    (h, i) =>
      yardageTee?.yardages?.[i] ??
      fallbackTee?.yardages?.[i] ??
      h.yardage,
  );
  const totalYards = displayedYardages.reduce<number>(
    (s, y) => s + (y || 0),
    0,
  );
  const hasYardage = totalYards > 0;

  // Per-hole text colour for the Yds row. Only combo tees with a defined
  // secondaryHoles list paint the yardage in tee colour — solid tees
  // leave the cell at the default muted colour.
  const yardageColorFor = (holeNumber: number): string | undefined => {
    if (
      !yardageTee?.secondaryHoles ||
      !yardageTee.colors ||
      yardageTee.colors.length < 2
    )
      return undefined;
    return yardageTee.secondaryHoles.includes(holeNumber)
      ? yardageTee.colors[1]
      : yardageTee.colors[0];
  };

  // Per-player course handicap, indexed by player slot. strokesFor below
  // reads from this so the WHS math runs in one place. Always computed
  // from the stored Player.handicap so the gold ★ row stays consistent
  // with each player's actual handicap.
  // Sized to cover the whole roster, not just MAX_PLAYERS — a read-only
  // combined tournament card stacks every group's players (well beyond 5), and
  // each row's stroke allocation is indexed by player position.
  const rosterLen = Math.max(MAX_PLAYERS, players.length);
  const playerCourseHcps: number[] = Array.from(
    { length: rosterLen },
    (_, i) => {
      const p = players[i];
      if (!p || p.handicap == null) return 0;
      const tee = tees.find((t) => t.id === p.tee);
      return courseHandicap(p.handicap, tee?.slope, tee?.rating, totalPar);
    },
  );

  // Second handicap pass used when any active game is playing off the
  // lowest handicap. The green ✪ rendering in each score cell
  // (and the matching badge next to the player's name) tracks this
  // adjusted allocation so the user can see at a glance which holes
  // they get shots on under that policy. When no playOffLow game is
  // running we skip the math entirely.
  //
  // The lowest player must play off 0 regardless of slope/rating —
  // courseHandicap can drive the CH below zero on courses where the
  // rating is below par, so the result is floored at 0.
  const playOffLowActive = !!games?.some(
    (g) => g.enabled && g.options?.handicapMode === "playOffLow",
  );
  const adjustedPlayers = playOffLowActive
    ? adjustHandicaps(players, "playOffLow")
    : players;
  const playerCourseHcpsLow: number[] = Array.from(
    { length: rosterLen },
    (_, i) => {
      if (!playOffLowActive) return 0;
      const p = adjustedPlayers[i];
      if (!p || p.handicap == null) return 0;
      const tee = tees.find((t) => t.id === p.tee);
      return Math.max(
        0,
        courseHandicap(p.handicap, tee?.slope, tee?.rating, totalPar),
      );
    },
  );

  // Default to 4 player rows on the scorecard. The 5th row only appears
  // once a 5th player has been added (via the Players modal). When the roster
  // is locked (tournament / read-only history) we show exactly the players —
  // no empty "+ Add player" padding rows, since none can be added.
  const visibleRows = rosterLocked
    ? players.length
    : Math.min(MAX_PLAYERS, Math.max(DEFAULT_VISIBLE_ROWS, players.length));
  const playerSlots: (Player | null)[] = Array.from(
    { length: visibleRows },
    (_, i) => players[i] || null,
  );

  // Group divider headers, keyed by the player index they precede (combined
  // tournament card only — empty otherwise).
  const groupBreakAt = new Map<number, string>(
    (groupBreaks ?? []).map((b) => [b.startIndex, b.label]),
  );

  const beginEditName = (idx: number) => {
    setEditingNameIdx(idx);
    setNameDraft(players[idx]?.name || "");
  };

  const commitName = (idx: number) => {
    const trimmed = nameDraft.trim();
    setEditingNameIdx(null);
    setNameDraft("");
    if (!trimmed) {
      // empty string clears the slot if it existed
      if (players[idx]) {
        const next = players.slice();
        next.splice(idx, 1);
        setPlayers(next);
      }
      return;
    }
    const next = players.slice();
    if (next[idx]) {
      next[idx] = { ...next[idx], name: trimmed };
    } else {
      next[idx] = { id: newPlayerID(), name: trimmed, tee: defaultTeeId };
    }
    setPlayers(next);
  };

  const saveScore = (
    gross: number,
    putts?: number,
    bunkerUd?: boolean,
  ) => {
    if (!scoreCell) return;
    const { player, hole, rect } = scoreCell;

    // Fire the picker whenever a Par 3 score commits and there's no
    // recorded selection yet. The "no existing selection" check is the
    // sole gate — we deliberately don't restrict to the first scorer
    // any more, because clearScore (below) removes the selection so a
    // user can correct a mis-tap, and the re-entered score might come
    // from any player whether or not others have already scored.
    // Dismissing the picker by tapping outside commits the default
    // ("None"), so the selection map is populated either way and we
    // don't re-prompt on subsequent edits.
    const needsOoslerPrompt =
      ooslerEnabled &&
      hole.par === 3 &&
      !(ooslerSelections && hole.number in ooslerSelections);

    // Open the FIRST per-hole picker that needs a decision; the rest chain
    // after it (see chainAfter): Oosler (Par 3s) → Wolf → Bingo Bango Bongo.
    const needsWolfPrompt =
      wolfEnabled &&
      !needsOoslerPrompt &&
      !(wolfSelections && hole.number in wolfSelections);

    const needsBingoPrompt =
      bbbEnabled &&
      !needsOoslerPrompt &&
      !needsWolfPrompt &&
      !(bingoSelections && hole.number in bingoSelections);

    setScores((prev) => ({
      ...prev,
      [player.id]: {
        ...(prev[player.id] || {}),
        [hole.number]: { gross, putts, bunkerUd },
      },
    }));
    setScoreCell(null);
    if (needsOoslerPrompt) setOoslerPrompt({ hole, anchorRect: rect });
    else if (needsWolfPrompt) setWolfPrompt({ hole, anchorRect: rect });
    else if (needsBingoPrompt) setBingoPrompt({ hole, anchorRect: rect });
  };

  // When several per-hole games are on, the pickers chain in play order on a
  // single score entry: Oosler (Par 3s) → Wolf → Bingo Bango Bongo. After one
  // closes, open the next that still needs a pick for this hole. The big
  // coloured header on each makes it clear which game is being entered.
  const chainAfter = (
    hole: Hole,
    rect: DOMRect,
    after: "oosler" | "wolf",
  ) => {
    if (
      after === "oosler" &&
      wolfEnabled &&
      !(wolfSelections && hole.number in wolfSelections)
    ) {
      setWolfPrompt({ hole, anchorRect: rect });
      return;
    }
    if (bbbEnabled && !(bingoSelections && hole.number in bingoSelections)) {
      setBingoPrompt({ hole, anchorRect: rect });
    }
  };

  const saveOoslerSelection = (playerId: string | null) => {
    const prompt = ooslerPrompt;
    if (!prompt || !onOoslerSelectionsChange) {
      setOoslerPrompt(null);
      return;
    }
    onOoslerSelectionsChange({
      ...(ooslerSelections || {}),
      [prompt.hole.number]: playerId,
    });
    setOoslerPrompt(null);
    chainAfter(prompt.hole, prompt.anchorRect, "oosler");
  };

  const saveWolfSelection = (selection: WolfSelection) => {
    const prompt = wolfPrompt;
    if (!prompt || !onWolfSelectionsChange) {
      setWolfPrompt(null);
      return;
    }
    onWolfSelectionsChange({
      ...(wolfSelections || {}),
      [prompt.hole.number]: selection,
    });
    setWolfPrompt(null);
    chainAfter(prompt.hole, prompt.anchorRect, "wolf");
  };

  const saveBingoSelection = (selection: BingoSelection) => {
    if (!bingoPrompt || !onBingoSelectionsChange) {
      setBingoPrompt(null);
      return;
    }
    onBingoSelectionsChange({
      ...(bingoSelections || {}),
      [bingoPrompt.hole.number]: selection,
    });
    setBingoPrompt(null);
  };

  const clearScore = () => {
    if (!scoreCell) return;
    const { player, hole } = scoreCell;
    setScores((prev) => {
      const playerScores = { ...(prev[player.id] || {}) };
      delete playerScores[hole.number];
      return { ...prev, [player.id]: playerScores };
    });
    // Reset the Oosler selection for this hole when a Par-3 score is
    // cleared so the picker re-fires on the next score entry. Lets a
    // user correct a mis-tapped Oosler by clearing and re-entering.
    if (
      ooslerEnabled &&
      hole.par === 3 &&
      ooslerSelections &&
      hole.number in ooslerSelections &&
      onOoslerSelectionsChange
    ) {
      const next = { ...ooslerSelections };
      delete next[hole.number];
      onOoslerSelectionsChange(next);
    }
    // Same correction path for Wolf: clearing a score frees the hole's Wolf
    // decision so the picker re-fires on the next score entry.
    if (
      wolfEnabled &&
      wolfSelections &&
      hole.number in wolfSelections &&
      onWolfSelectionsChange
    ) {
      const next = { ...wolfSelections };
      delete next[hole.number];
      onWolfSelectionsChange(next);
    }
    if (
      bbbEnabled &&
      bingoSelections &&
      hole.number in bingoSelections &&
      onBingoSelectionsChange
    ) {
      const next = { ...bingoSelections };
      delete next[hole.number];
      onBingoSelectionsChange(next);
    }
    setScoreCell(null);
  };

  const playerTotal = (p: Player | null): number => {
    if (!p) return 0;
    const ps = scores[p.id] || {};
    return Object.values(ps).reduce((s, sc) => s + (sc.gross || 0), 0);
  };

  // Sum a player's gross over a given subset of holes (front 9 / back 9).
  const playerNineTotal = (p: Player | null, holes: Hole[]): number => {
    if (!p) return 0;
    const ps = scores[p.id] || {};
    return holes.reduce((s, h) => s + (ps[h.number]?.gross || 0), 0);
  };

  // Front 9 / back 9 splits drive the new OUT / IN total columns. For
  // courses other than 18 holes the back array may be empty — we keep
  // the simple split so the grid still works (rendered cell counts
  // match the column template).
  const front = course.holes.slice(0, 9);
  const back = course.holes.slice(9);
  const parOut = front.reduce((s, h) => s + h.par, 0);
  const parIn = back.reduce((s, h) => s + h.par, 0);
  const yardsOut = displayedYardages
    .slice(0, 9)
    .reduce<number>((s, y) => s + (y || 0), 0);
  const yardsIn = displayedYardages
    .slice(9)
    .reduce<number>((s, y) => s + (y || 0), 0);

  return (
    <div
      className={`cg-scorecard${readOnly ? " cg-scorecard-readonly" : ""}${
        lockRoster ? " cg-roster-locked" : ""
      }`}
    >
      <div className="cg-topbar">
        <div className="cg-topbar-course" onClick={onChangeCourse}>
          <span className="cg-topbar-name">{course.name}</span>
          <span className="cg-topbar-change">
            {readOnly ? "back" : "change"}
          </span>
        </div>
        {!readOnly && (
          <div className="cg-topbar-actions">
            {/* Players (roster/handicaps/tees) is hidden when the roster is
                locked by a tournament; Games + Leaderboard stay available. */}
            {!lockRoster && (
              <button onClick={() => setOpenModal("players")}>Players</button>
            )}
            <button onClick={() => setOpenModal("games")}>Games</button>
            <button onClick={() => setOpenModal("leaderboard")}>
              Leaderboard
            </button>
          </div>
        )}
      </div>

      <div className="cg-scrollwrap">
        <div
          className="cg-grid"
          style={{
            gridTemplateColumns: `var(--cg-name-col) repeat(${front.length}, minmax(var(--cg-hole-col), 1fr)) var(--cg-total-col) repeat(${back.length}, minmax(var(--cg-hole-col), 1fr)) var(--cg-total-col) var(--cg-total-col)`,
          }}
        >
          {/* Hole numbers */}
          <div className="cg-cell cg-row-head cg-col-name cg-sticky-left">
            Hole
          </div>
          {front.map((h) => (
            <div key={`hn-${h.number}`} className="cg-cell cg-row-head">
              {h.number}
            </div>
          ))}
          <div className="cg-cell cg-row-head cg-total-col cg-out-col">Out</div>
          {back.map((h) => (
            <div key={`hn-${h.number}`} className="cg-cell cg-row-head">
              {h.number}
            </div>
          ))}
          <div className="cg-cell cg-row-head cg-total-col">In</div>
          <div className="cg-cell cg-row-head cg-total-col">Tot</div>

          {/* Yardage */}
          {hasYardage && (
            <>
              <div
                className={`cg-cell cg-row-info cg-col-name cg-sticky-left${
                  rosterLocked ? "" : " cg-yds-trigger"
                }`}
                onClick={rosterLocked ? undefined : () => setOpenModal("tees")}
                title={rosterLocked ? undefined : "Change tee for the Yds row"}
              >
                <span>Yds</span>
                <TeeDot
                  tee={yardageTee}
                  size={12}
                  className="cg-name-tee-dot"
                />
              </div>
              {front.map((h, i) => {
                const color = yardageColorFor(h.number);
                return (
                  <div
                    key={`yd-${h.number}`}
                    className="cg-cell cg-row-info cg-yards"
                    style={color ? { color } : undefined}
                  >
                    {displayedYardages[i] ?? ""}
                  </div>
                );
              })}
              <div className="cg-cell cg-row-info cg-total-col cg-out-col cg-yards">
                {yardsOut || ""}
              </div>
              {back.map((h, i) => {
                const color = yardageColorFor(h.number);
                return (
                  <div
                    key={`yd-${h.number}`}
                    className="cg-cell cg-row-info cg-yards"
                    style={color ? { color } : undefined}
                  >
                    {displayedYardages[i + front.length] ?? ""}
                  </div>
                );
              })}
              <div className="cg-cell cg-row-info cg-total-col cg-yards">
                {yardsIn || ""}
              </div>
              <div className="cg-cell cg-row-info cg-total-col cg-yards">
                {totalYards}
              </div>
            </>
          )}

          {/* SI */}
          <div className="cg-cell cg-row-info cg-col-name cg-sticky-left">
            SI
          </div>
          {front.map((h) => (
            <div key={`si-${h.number}`} className="cg-cell cg-row-info cg-si">
              {h.si}
            </div>
          ))}
          <div className="cg-cell cg-row-info cg-total-col cg-out-col" />
          {back.map((h) => (
            <div key={`si-${h.number}`} className="cg-cell cg-row-info cg-si">
              {h.si}
            </div>
          ))}
          <div className="cg-cell cg-row-info cg-total-col" />
          <div className="cg-cell cg-row-info cg-total-col" />

          {/* Par */}
          <div className="cg-cell cg-row-info cg-col-name cg-sticky-left">
            Par
          </div>
          {front.map((h) => (
            <div key={`par-${h.number}`} className="cg-cell cg-row-info">
              {h.par}
            </div>
          ))}
          <div className="cg-cell cg-row-info cg-total-col cg-out-col">{parOut}</div>
          {back.map((h) => (
            <div key={`par-${h.number}`} className="cg-cell cg-row-info">
              {h.par}
            </div>
          ))}
          <div className="cg-cell cg-row-info cg-total-col">{parIn}</div>
          <div className="cg-cell cg-row-info cg-total-col">{totalPar}</div>

          {/* Player rows */}
          {playerSlots.map((player, idx) => (
            <React.Fragment key={`p-${idx}`}>
              {groupBreakAt.has(idx) && (
                <div className="cg-cell cg-group-divider">
                  {groupBreakAt.get(idx)}
                </div>
              )}
              <div
                className={`cg-cell cg-col-name cg-sticky-left ${
                  player ? "cg-name-filled" : "cg-name-empty"
                }`}
                onClick={rosterLocked ? undefined : () => beginEditName(idx)}
              >
                {editingNameIdx === idx ? (
                  <input
                    autoFocus
                    className="cg-name-input"
                    value={nameDraft}
                    onChange={(e) => setNameDraft(e.target.value)}
                    onBlur={() => commitName(idx)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") commitName(idx);
                      if (e.key === "Escape") {
                        setEditingNameIdx(null);
                        setNameDraft("");
                      }
                    }}
                    placeholder="Name or initials"
                  />
                ) : player ? (
                  <>
                    <span>{player.name}</span>
                    {player.handicap != null && (
                      <span className="cg-name-hcp">
                        ({playerCourseHcps[idx]})
                      </span>
                    )}
                    {playOffLowActive && player.handicap != null && (
                      <span className="cg-name-hcp cg-name-hcp-low">
                        ({playerCourseHcpsLow[idx]})
                      </span>
                    )}
                    <TeeDot
                      tee={tees.find((t) => t.id === player.tee)}
                      size={12}
                      className="cg-name-tee-dot"
                    />
                  </>
                ) : (
                  <span className="cg-add-player">+ Add player</span>
                )}
              </div>

              {front.map((hole) => {
                const score =
                  (player && scores[player.id]?.[hole.number]) || null;
                const strokes = strokesFor(playerCourseHcps[idx], hole);
                return (
                  <div
                    key={`s-${idx}-${hole.number}`}
                    className={`cg-cell cg-score-cell ${
                      readOnly
                        ? "cg-score-readonly"
                        : player
                          ? "cg-score-active"
                          : "cg-score-disabled"
                    }`}
                    onClick={
                      readOnly
                        ? undefined
                        : (e) => {
                            if (!player) return;
                            const rect = (
                              e.currentTarget as HTMLDivElement
                            ).getBoundingClientRect();
                            setScoreCell({ player, hole, rect });
                          }
                    }
                  >
                    {strokes > 0 && (() => {
                      // Single row sized to the Full-handicap
                      // allocation. When any active game is in
                      // Play-off-Low mode the subset of strokes that
                      // also apply under that policy renders as a
                      // green ✪; the remaining (Full-only) strokes
                      // stay as gold ★. So on a hole where Full = 2
                      // and Low = 1, the user sees one green ✪
                      // followed by one gold ★.
                      const visible = Math.min(3, strokes);
                      const lowAll = playOffLowActive
                        ? strokesFor(playerCourseHcpsLow[idx], hole)
                        : 0;
                      const lowVisible = Math.min(visible, lowAll);
                      const goldVisible = visible - lowVisible;
                      return (
                        <span className="cg-stroke">
                          {lowVisible > 0 && (
                            <span className="cg-stroke-low">
                              {"✪".repeat(lowVisible)}
                            </span>
                          )}
                          {"★".repeat(goldVisible)}
                        </span>
                      );
                    })()}
                    {score ? (
                      <span
                        className={`cg-score-num${
                          score.gross <= hole.par - 1
                            ? " cg-score-birdie"
                            : ""
                        }`}
                      >
                        {score.gross}
                      </span>
                    ) : (
                      <span className="cg-score-blank" />
                    )}
                  </div>
                );
              })}
              <div className="cg-cell cg-row-total cg-total-col cg-out-col">
                {player ? playerNineTotal(player, front) || "" : ""}
              </div>
              {back.map((hole) => {
                const score =
                  (player && scores[player.id]?.[hole.number]) || null;
                const strokes = strokesFor(playerCourseHcps[idx], hole);
                return (
                  <div
                    key={`s-${idx}-${hole.number}`}
                    className={`cg-cell cg-score-cell ${
                      readOnly
                        ? "cg-score-readonly"
                        : player
                          ? "cg-score-active"
                          : "cg-score-disabled"
                    }`}
                    onClick={
                      readOnly
                        ? undefined
                        : (e) => {
                            if (!player) return;
                            const rect = (
                              e.currentTarget as HTMLDivElement
                            ).getBoundingClientRect();
                            setScoreCell({ player, hole, rect });
                          }
                    }
                  >
                    {strokes > 0 && (() => {
                      // Single row sized to the Full-handicap
                      // allocation. When any active game is in
                      // Play-off-Low mode the subset of strokes that
                      // also apply under that policy renders as a
                      // green ✪; the remaining (Full-only) strokes
                      // stay as gold ★. So on a hole where Full = 2
                      // and Low = 1, the user sees one green ✪
                      // followed by one gold ★.
                      const visible = Math.min(3, strokes);
                      const lowAll = playOffLowActive
                        ? strokesFor(playerCourseHcpsLow[idx], hole)
                        : 0;
                      const lowVisible = Math.min(visible, lowAll);
                      const goldVisible = visible - lowVisible;
                      return (
                        <span className="cg-stroke">
                          {lowVisible > 0 && (
                            <span className="cg-stroke-low">
                              {"✪".repeat(lowVisible)}
                            </span>
                          )}
                          {"★".repeat(goldVisible)}
                        </span>
                      );
                    })()}
                    {score ? (
                      <span
                        className={`cg-score-num${
                          score.gross <= hole.par - 1
                            ? " cg-score-birdie"
                            : ""
                        }`}
                      >
                        {score.gross}
                      </span>
                    ) : (
                      <span className="cg-score-blank" />
                    )}
                  </div>
                );
              })}
              <div className="cg-cell cg-row-total cg-total-col">
                {player ? playerNineTotal(player, back) || "" : ""}
              </div>
              <div className="cg-cell cg-row-total cg-total-col">
                {player ? playerTotal(player) || "" : ""}
              </div>
            </React.Fragment>
          ))}
        </div>
      </div>

      {scoreCell && (
        <ScoreScrollPicker
          player={scoreCell.player}
          hole={scoreCell.hole}
          existing={scores[scoreCell.player.id]?.[scoreCell.hole.number]}
          anchorRect={scoreCell.rect}
          // Putts wheel visibility:
          //   • Putting game ON → wheel on every hole (it tracks putts
          //     all round long).
          //   • Oosler ON, Putting OFF → only on Par 3s, since Oosler
          //     only needs putt context to settle Par-3 outcomes.
          //   • Neither → hidden, picker stays tight on score only.
          showPutts={
            puttingEnabled ||
            (ooslerEnabled && scoreCell.hole.par === 3)
          }
          onSave={saveScore}
          onClear={clearScore}
          onClose={() => setScoreCell(null)}
        />
      )}

      {ooslerPrompt && (
        <OoslerSelectionModal
          holeNumber={ooslerPrompt.hole.number}
          players={players}
          anchorRect={ooslerPrompt.anchorRect}
          onSelect={saveOoslerSelection}
          onClose={() => setOoslerPrompt(null)}
        />
      )}

      {wolfPrompt && (
        <WolfSelectionModal
          holeNumber={wolfPrompt.hole.number}
          wolfId={wolfForHole(wolfPrompt.hole.number)?.id || ""}
          wolfName={wolfForHole(wolfPrompt.hole.number)?.name || ""}
          players={players}
          blindEnabled={wolfBlindEnabled}
          anchorRect={wolfPrompt.anchorRect}
          onSelect={saveWolfSelection}
          onClose={() => setWolfPrompt(null)}
        />
      )}

      {bingoPrompt && (
        <BingoSelectionModal
          holeNumber={bingoPrompt.hole.number}
          players={players}
          anchorRect={bingoPrompt.anchorRect}
          onComplete={saveBingoSelection}
          onClose={() => setBingoPrompt(null)}
        />
      )}

      {openModal === "players" && (
        <PlayersModal
          players={players}
          max={MAX_PLAYERS}
          tees={tees}
          defaultTeeId={defaultTeeId}
          onChange={setPlayers}
          onClose={() => setOpenModal(null)}
        />
      )}

      {openModal === "tees" && (
        <TeesModal
          tees={tees}
          selectedId={yardageTeeId}
          onSelect={(id) => {
            setDisplayTeeId(id);
            setOpenModal(null);
          }}
          onClose={() => setOpenModal(null)}
        />
      )}

      {openModal === "games" && onGamesChange && (
        <GamesModal
          games={games || []}
          players={players}
          onChange={onGamesChange}
          onClose={() => setOpenModal(null)}
        />
      )}

      {openModal === "leaderboard" && (
        <LeaderboardModal
          players={players}
          scores={scores}
          totalPar={totalPar}
          course={course}
          games={games}
          ooslerSelections={ooslerSelections}
          wolfSelections={wolfSelections}
          bingoSelections={bingoSelections}
          onGamesChange={onGamesChange}
          onEndRound={() => {
            setOpenModal(null);
            onEndRound();
          }}
          tournamentGames={tournamentGames}
          tournamentId={tournamentId}
          tournamentGroupId={tournamentGroupId}
          tournamentGroupName={tournamentGroupName}
          onClose={() => setOpenModal(null)}
        />
      )}
    </div>
  );
}
